import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import api from '../lib/api';

interface BlastRedirectProps {
  type: 'school' | 'user' | 'club' | 'band';
}

interface LookupResult {
  found: boolean;
  record?: {
    id: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    assignedToCurrentUser: boolean;
  };
}

export const BlastRedirect = ({ type }: BlastRedirectProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'not-found'>('loading');

  const blastId =
    type === 'school'
      ? params.blastSchoolId
      : type === 'club'
        ? params.blastClubId
        : type === 'band'
          ? params.blastBandId
          : params.blastUserId;

  useEffect(() => {
    if (!blastId) {
      setStatus('not-found');
      return;
    }

    const lookup = async () => {
      try {
        const endpoint = `/api/blast-lookup/${type}/${blastId}`;

        const response = await api.get<LookupResult>(endpoint);
        const data = response.data;

        if (!data.found) {
          setStatus('not-found');
          return;
        }

        const path = type === 'user'
          ? `/people/${data.record!.id}`
          : `/organizations/${data.record!.id}`;
        navigate(path, { replace: true });
      } catch (error) {
        setStatus('not-found');
      }
    };

    lookup();
  }, [blastId, type, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Search className="h-8 w-8 text-blast-600 dark:text-blast-400 mx-auto animate-pulse" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Redirecting...</p>
        </div>
      </div>
    );
  }

  // not-found
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="max-w-md mx-auto text-center p-8">
        <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          This {type === 'user' ? 'contact' : type} was not found in the CRM.
        </p>
        <button
          onClick={() => navigate('/', { replace: true })}
          className="px-4 py-2 bg-blast-600 text-white rounded-lg hover:bg-blast-700 transition-colors"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
};
